import { envType } from '@atlassiansox/analytics-web-client';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { AnyAri } from '@atlassian/ari';

import { ALL_SUPPORTED_OBJECTS } from './constants';
import type { GlobalObjectType } from './types';

export const getGlobalObjectType = (ari: string): GlobalObjectType => {
	const parsedAri = AnyAri.parse(ari);
	return `${parsedAri.resourceOwner}-${parsedAri.resourceType}` as GlobalObjectType;
};

/**
 * Whether an object supports being opened in a preview panel, derived from its ARI object type.
 * This is the standalone form of the availability check surfaced by `useGlobalObjectPreviewPanels`;
 */
export const isPreviewPanelAvailable = (ari: string, enabled?: boolean): boolean => {
	const panelType = getGlobalObjectType(ari);
	const panelEnabled = (enabled ?? true) && ALL_SUPPORTED_OBJECTS.includes(panelType);

	if (panelType === 'confluence-blogpost') {
		return (
			panelEnabled &&
			FeatureGates.getExperimentValue(
				'townsquare_enable_discover_preview_panels_exp',
				'cohort',
				'control',
				{ fireExperimentExposure: false },
			) !== 'control'
		);
	}

	return panelEnabled;
};

// Helper function to check if we're in an iframe - exported for testing
export const isInIframe = (): boolean => {
	try {
		return window !== window.top;
	} catch {
		// If we can't access window.top due to cross-origin restrictions, assume we're in an iframe
		return true;
	}
};

export const asGlobalObjectType = (value: string): GlobalObjectType => {
	if (!ALL_SUPPORTED_OBJECTS.includes(value as GlobalObjectType)) {
		throw new Error(`Unsupported global object type: ${value}`);
	}

	return value as GlobalObjectType;
};

/**
 * Constructs a proper embed URL by extracting query parameters and reconstructing the URL
 * @param url - The original URL
 * @param supportedObjects - Array of supported object types for previewPanels param
 * @returns A properly formatted embed URL with all query parameters at the end
 */
export const buildIssueViewEmbedUrl = (
	url: string,
	supportedObjects: string[],
	rootSourceScreen?: string,
): string => {
	try {
		const urlObj = new URL(url);

		if (fg('pol-21166-fix-404-on-jpd-preview')) {
			if (isJpdObject(urlObj)) {
				return buildJpdEmbedUrl(urlObj);
			}
		}

		// Ensure there's no trailing slash before appending '/embed'
		urlObj.pathname = urlObj.pathname.replace(/\/$/, '') + '/embed';

		// Add previewPanels to existing search params
		urlObj.searchParams.set('previewPanels', supportedObjects.join(','));

		// Availability check only — this decides whether to append the previewRootSourceScreen
		// URL param, NOT whether the user is accessing the Capacity planning experience. We
		// therefore read the GA experiment WITHOUT firing a Statsig exposure
		// (fireExperimentExposure: false); exposure is fired at the point the user actually
		// opens the Capacity experience, keeping the GA experiment's population clean.
		//
		// The OR still short-circuits: when the legacy EAP gate is ON the experiment is never
		// read at all. EAP customers remain unconditionally enabled.
		if (
			rootSourceScreen &&
			(fg('add_capacity_tab_to_the_board_horizontal_nav') ||
				FeatureGates.getExperimentValue('individual_capacity_planning_ga', 'isEnabled', false, {
					fireExperimentExposure: false,
				}))
		) {
			urlObj.searchParams.set('previewRootSourceScreen', rootSourceScreen);
		}
		// Return the full URL as a string
		return urlObj.toString();
	} catch {
		// Fallback for invalid URLs.
		//
		// Availability check only (same semantics as the happy path above): this decides
		// whether to append previewRootSource, NOT whether the user is accessing the Capacity
		// planning experience. We read the GA experiment WITHOUT firing a Statsig exposure
		// (fireExperimentExposure: false). The OR still short-circuits when the legacy EAP gate
		// is ON, preserving a clean GA-experiment population.
		if (
			fg('add_capacity_tab_to_the_board_horizontal_nav') ||
			FeatureGates.getExperimentValue('individual_capacity_planning_ga', 'isEnabled', false, {
				fireExperimentExposure: false,
			})
		) {
			const encodedRootSource = rootSourceScreen
				? `&previewRootSource=${encodeURIComponent(rootSourceScreen)}`
				: '';
			return `${url}/embed?previewPanels=${encodeURIComponent(supportedObjects.join(','))}${encodedRootSource}`;
		} else {
			return `${url}/embed?previewPanels=${encodeURIComponent(supportedObjects.join(','))}`;
		}
	}
};

export const getAnalyticsEnv = (hostname: string): envType.DEV | envType.PROD => {
	if (hostname.includes('localhost') || hostname.includes('jira-dev')) {
		return envType.DEV;
	}

	return envType.PROD;
};

const isJpdObject = (urlObj: URL) => {
	return urlObj.pathname.includes('/jira/polaris/projects/');
};

const buildJpdEmbedUrl = (urlObj: URL) => {
	const ideaKey = urlObj.searchParams.get('selectedIssue');
	urlObj.searchParams.delete('selectedIssue');

	urlObj.pathname = `/browse/${ideaKey}/embed`;

	return urlObj.toString();
};
